import { useEffect, useMemo, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { mapProfile } from "@/lib/adapt";
import { ProfileCard } from "@/components/ProfileCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { SECTS, PRAYER, MARITAL, COUNTRIES } from "@/constants/options";
import { SearchableSelect } from "@/components/SearchableSelect";
import { toast } from "sonner";
import { Search as SearchIcon, SlidersHorizontal, X } from "lucide-react";

const ANY = "__any__";
const MARITAL_PILLS = ["Any", "Never Married", "Divorced", "Widowed"];
const norm = (s) => (s ?? "").toString().toLowerCase().replace(/\s+/g, " ").trim();

function Filters({ f, setF }) {
  const sel = (k, options, ph) => (
    <div className="space-y-2">
      <Label>{ph}</Label>
      <Select value={f[k] || ANY} onValueChange={(v) => setF({ ...f, [k]: v === ANY ? "" : v })}>
        <SelectTrigger data-testid={`filter-${k}`}><SelectValue placeholder="Any" /></SelectTrigger>
        <SelectContent><SelectItem value={ANY}>Any</SelectItem>{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2"><Label>Country</Label><SearchableSelect value={f.country} onChange={(v) => setF({ ...f, country: v })} options={COUNTRIES} placeholder="Any" testid="filter-country" /></div>
        <div className="space-y-2"><Label>City</Label><Input value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} data-testid="filter-city" placeholder="Any" /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2"><Label>Min age</Label><Input type="number" value={f.age_min} onChange={(e) => setF({ ...f, age_min: e.target.value })} data-testid="filter-age-min" /></div>
        <div className="space-y-2"><Label>Max age</Label><Input type="number" value={f.age_max} onChange={(e) => setF({ ...f, age_max: e.target.value })} data-testid="filter-age-max" /></div>
      </div>
      {sel("sect", SECTS, "Sect / School")}
      {sel("prayer_habit", PRAYER, "Prayer habit")}
      {sel("marital_status", MARITAL, "Marital status")}
      <div className="flex items-center justify-between rounded-lg border p-3">
        <Label>Open to relocation</Label>
        <Switch checked={f.relocation} onCheckedChange={(v) => setF({ ...f, relocation: v })} data-testid="filter-relocation" />
      </div>
    </div>
  );
}

const empty = { country: "", city: "", age_min: "", age_max: "", sect: "", prayer_habit: "", marital_status: "", relocation: false };

export default function SearchPage() {
  const [f, setF] = useState(empty);          // sidebar filters + marital chip (shared state)
  const [qInput, setQInput] = useState("");   // text field value
  const [appliedQ, setAppliedQ] = useState(""); // committed text query (Enter / Search)
  const [raw, setRaw] = useState(null);       // full filtered set from backend (null = loading)
  const [loading, setLoading] = useState(false);
  const [openFilters, setOpenFilters] = useState(false);

  // Fetch the full set matching the structured sidebar filters (server-side).
  // Text search is applied on top client-side so it always searches every
  // matching profile, not just the current page.
  const run = useCallback(async () => {
    setRaw(null); setLoading(true);
    const map = { age_min: "ageMin", age_max: "ageMax", marital_status: "maritalStatus", prayer_habit: "prayerFrequency" };
    const params = { page: 1, limit: 100 };
    Object.entries(f).forEach(([k, v]) => { if (v !== "" && v !== false) params[map[k] || k] = v; });
    try {
      const { data } = await api.get("/search", { params });
      setRaw((data.profiles || data.results || data.items || []).map(mapProfile).filter((p) => !p.hidden));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setRaw([]); }
    finally { setLoading(false); }
  }, [f]);

  useEffect(() => { run(); }, [run]);

  const results = useMemo(() => {
    if (raw === null) return null;
    const nq = norm(appliedQ);
    if (!nq) return raw;
    return raw.filter((p) => [p.name, p.firstName, p.lastName, p.profession, p.city, p.country].some((v) => norm(v).includes(nq)));
  }, [raw, appliedQ]);

  const doSearch = () => setAppliedQ(qInput.trim());
  const clearAll = () => { setF(empty); setQInput(""); setAppliedQ(""); };
  const marital = f.marital_status || "Any";

  // Active-criteria chips
  const chips = [];
  if (appliedQ) chips.push({ key: "q", label: `“${appliedQ}”`, remove: () => { setQInput(""); setAppliedQ(""); } });
  if (f.marital_status) chips.push({ key: "marital", label: f.marital_status, remove: () => setF((x) => ({ ...x, marital_status: "" })) });
  if (f.country) chips.push({ key: "country", label: f.country, remove: () => setF((x) => ({ ...x, country: "" })) });
  if (f.city) chips.push({ key: "city", label: f.city, remove: () => setF((x) => ({ ...x, city: "" })) });
  if (f.sect) chips.push({ key: "sect", label: f.sect, remove: () => setF((x) => ({ ...x, sect: "" })) });
  if (f.prayer_habit) chips.push({ key: "prayer", label: f.prayer_habit, remove: () => setF((x) => ({ ...x, prayer_habit: "" })) });
  if (f.age_min || f.age_max) chips.push({ key: "age", label: `Age ${f.age_min || "18"}–${f.age_max || "99"}`, remove: () => setF((x) => ({ ...x, age_min: "", age_max: "" })) });
  if (f.relocation) chips.push({ key: "reloc", label: "Open to relocation", remove: () => setF((x) => ({ ...x, relocation: false })) });

  return (
    <div>
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-700 to-emerald-900 text-amber-300 shadow-sm"><SearchIcon className="h-6 w-6" /></div>
        <div>
          <h1 className="font-serif text-3xl font-bold text-emerald-950 sm:text-4xl">Search profiles</h1>
          <p className="text-sm text-slate-500">Find members by name, place, values and more</p>
        </div>
      </div>

      <div className="mb-4 flex gap-2">
        <div className="relative flex-1">
          <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={qInput} onChange={(e) => setQInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doSearch()} data-testid="search-input" placeholder="Search by name, profession, city, country…" className="rounded-full pl-9" />
        </div>
        <Button onClick={doSearch} disabled={loading} data-testid="search-btn" className="rounded-full bg-emerald-800 px-6 text-white hover:bg-emerald-900">Search</Button>
        <Sheet open={openFilters} onOpenChange={setOpenFilters}>
          <SheetTrigger asChild><Button variant="outline" className="rounded-full border-emerald-800/30 text-emerald-900 hover:bg-mint" data-testid="filters-btn"><SlidersHorizontal className="mr-1.5 h-4 w-4" /> Filters</Button></SheetTrigger>
          <SheetContent side="right" className="w-80 overflow-y-auto bg-white">
            <SheetHeader><SheetTitle className="font-serif text-emerald-900">Advanced filters</SheetTitle></SheetHeader>
            <div className="mt-4"><Filters f={f} setF={setF} /></div>
            <div className="mt-5 flex gap-2">
              <Button variant="outline" onClick={() => setF(empty)} data-testid="filters-reset-btn" className="flex-1 rounded-full">Reset filters</Button>
              <Button onClick={() => setOpenFilters(false)} data-testid="filters-apply-btn" className="flex-1 rounded-full bg-emerald-800 text-white">Apply</Button>
            </div>
          </SheetContent>
        </Sheet>
      </div>

      {/* Quick marital pills */}
      <div className="mb-4 flex flex-wrap gap-2" data-testid="search-quick-pills">
        {MARITAL_PILLS.map((m) => {
          const active = marital === m || (m === "Any" && !f.marital_status);
          return (
            <button key={m} onClick={() => setF({ ...f, marital_status: m === "Any" ? "" : m })} data-testid={`quick-marital-${m.toLowerCase().replace(/\s/g, "-")}`}
              className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${active ? "bg-emerald-800 text-white" : "bg-white text-emerald-900/70 ring-1 ring-emerald-900/10 hover:bg-mint"}`}>
              {m}
            </button>
          );
        })}
      </div>

      {/* Active-criteria chips */}
      {chips.length > 0 && (
        <div className="mb-6 flex flex-wrap items-center gap-2" data-testid="search-active-chips">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Active:</span>
          {chips.map((c) => (
            <button key={c.key} onClick={c.remove} data-testid={`chip-${c.key}`}
              className="group inline-flex items-center gap-1.5 rounded-full bg-mint px-3 py-1.5 text-sm font-medium text-emerald-900 ring-1 ring-emerald-800/15 hover:bg-emerald-100">
              {c.label}
              <X className="h-3.5 w-3.5 text-emerald-700/70 group-hover:text-emerald-900" />
            </button>
          ))}
          <button onClick={clearAll} data-testid="search-clear-all" className="ml-1 rounded-full px-3 py-1.5 text-sm font-semibold text-red-600 hover:bg-red-50">Clear all</button>
        </div>
      )}

      {results === null ? (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">{[...Array(8)].map((_, i) => <Skeleton key={i} className="aspect-[3/4] rounded-3xl" />)}</div>
      ) : results.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-emerald-300 bg-white py-20 text-center" data-testid="search-empty">
          <SearchIcon className="mb-3 h-10 w-10 text-amber-500" />
          <h3 className="font-serif text-2xl font-semibold text-emerald-900">No profiles match your search</h3>
          <p className="mt-2 max-w-sm text-slate-500">
            {chips.length > 0 ? "Try removing a filter or search term, or " : "Try a different search term or "}
            <button onClick={clearAll} className="font-semibold text-emerald-700 underline hover:text-emerald-900">clear all criteria</button>.
          </p>
        </div>
      ) : (
        <>
          <p className="mb-3 text-sm text-slate-500" data-testid="search-count">{results.length} {results.length === 1 ? "profile" : "profiles"} found</p>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="search-results">
            {results.map((p, i) => <ProfileCard key={p.id + i} profile={p} index={i} showActions={false} />)}
          </div>
        </>
      )}
    </div>
  );
}
